'use client';

import { ReactNode, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { hasTamilVersion, toTamilPath, toEnglishPath } from '@/lib/taRoutes';
import { contactHref } from '@/lib/contactHref';
import { CITY_NAV } from '@/lib/cityNav';
import { ArrowRight, Menu, X, Mail, Phone, Check, MessageCircle } from 'lucide-react';
import {
  organizationJsonLd,
  websiteJsonLd,
  localBusinessJsonLd,
  hasBusinessPhone,
  hasBusinessWhatsApp,
  whatsAppHref,
  businessPhoneDisplay,
  BUSINESS_CONTACT,
} from '@/lib/marketingMetadata';
import { BrandSymbol } from '@/components/BrandMark';
import { Shadcn } from '@/components/ui/scope';
import { MARKETING_TOKENS, MARKETING_BG_TRANSLUCENT, SUPPORT_EMAIL } from './marketingTokens';

/**
 * Shared marketing chrome — used by /, /pricing, /security, /contact, /about,
 * /vs-tally, /vs-spreadsheet, /how-it-works, /modules, and the legal pages.
 *
 * Light, warm, premium surface — intentionally not theme-aware. Design tokens
 * live in ./marketingTokens (a plain module) so Server Components read the real
 * values; re-exported here so the long-standing
 * `import { MARKETING_TOKENS } from './MarketingShell'` call sites keep working.
 */
export { MARKETING_TOKENS, MARKETING_BG_TRANSLUCENT, SUPPORT_EMAIL } from './marketingTokens';

const SERIF = 'var(--font-serif), Georgia, serif';
const PRIMARY_TINT = 'rgba(var(--mk-primary-rgb,4,120,87),0.08)';

const NAV_LINKS: { label: string; href: string }[] = [
  // "How it works" leads: a builder who has not seen the product wants the
  // walkthrough before the module catalog.
  { label: 'How it works', href: '/how-it-works' },
  { label: 'Modules',  href: '/modules' },
  { label: 'Guides',   href: '/guides' },
  // NOT 'Tools'. It belongs here on the merits — a free calculator is the
  // strongest reason a first-time visitor has to stay — but this bar is full.
  // Measured at the lg breakpoint where it first renders: seven items put the
  // links container at 575px of a 1024px viewport, and an eighth takes it to
  // 642px, overflowing the page by 27px. Static width maths said it would fit;
  // a browser at 1024px said otherwise, which is why that is the only check
  // worth trusting here.
  //
  // Adding it means removing something — Security and Compare are the
  // candidates — and that is a product call, not a layout fix. Until then
  // /tools is reached from the footer sitewide, and the calculator from ten
  // contextual links.
  { label: 'Pricing',  href: '/pricing' },
  { label: 'Security', href: '/security' },
  { label: 'Compare',  href: '/vs-tally' },
  // Every link to the form carries its intent and the page it was clicked
  // from — see src/lib/contactHref.ts for why that was not optional.
  { label: 'Contact',  href: contactHref('demo', 'nav') },
];

const FOOTER_GROUPS: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: 'Product',
    links: [
      { label: 'How it works',     href: '/how-it-works' },
      { label: 'Modules',          href: '/modules' },
      { label: 'Full catalogue',   href: '/all-module' },
      { label: 'Pricing',          href: '/pricing' },
      { label: 'vs Tally',         href: '/vs-tally' },
      { label: 'vs Spreadsheets',  href: '/vs-spreadsheet' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About',   href: '/about' },
      { label: 'Contact', href: contactHref('demo', 'footer') },
      { label: 'Sign in', href: '/login' },
    ],
  },
  {
    // ── Why the city pages live in the footer ──────────────────────────
    // They were orphans: reachable from sitemap.xml and from each other,
    // and from nothing else on the site. Google found them, saw no internal
    // links, inferred they were unimportant, and parked all five in
    // "Discovered - currently not indexed". These are the highest-intent
    // pages on the domain, so they had the weakest crawl signal on the
    // domain, which is backwards. A sitewide footer link fixes exactly that.
    title: 'Tamil Nadu',
    links: [
      ...CITY_NAV.map((c) => ({ label: c.city, href: `/construction-erp-software/${c.slug}` })),
      { label: 'தமிழ்', href: '/ta' },
    ],
  },
  {
    title: 'Resources',
    links: [
      // The guides are the only pages on the site that answer a question
      // rather than describe the product, so they are the ones most likely to
      // be linked to from elsewhere. They need a sitewide link for the same
      // reason the city pages did — a page reachable only from the sitemap is
      // a page Google deprioritises.
      { label: 'Guides',         href: '/guides' },
      // Same argument as the guides, only more so: a free calculator is the
      // one page type a stranger links to without being asked, and a sitewide
      // link is the cheapest crawl signal it can be given.
      { label: 'Free tools',     href: '/tools' },
      { label: 'Security',       href: '/security' },
      { label: 'Privacy',        href: '/privacy' },
      { label: 'Terms',          href: '/terms' },
      { label: 'Cookie policy',  href: '/cookie-policy' },
      { label: 'Delete account', href: '/account-deletion' },
    ],
  },
];

/**
 * The Tamil chrome for the /ta tree.
 *
 * Product nouns stay in English on purpose — a Tamil Nadu builder says
 * Modules, Pricing and Security in English, and translating them into literary
 * Tamil makes the nav harder to use, not easier. Only the words that are
 * genuinely spoken in Tamil are translated. Same rule as the page copy.
 *
 * Links point at the Tamil page where one exists and at the English page
 * otherwise, so no link is a dead end while the tree is still filling in.
 */
const NAV_LINKS_TA: { label: string; href: string }[] = [
  { label: 'Modules',  href: '/ta/modules' },
  { label: 'விலை',     href: '/ta/pricing' },
  { label: 'ஊர் வாரியா', href: '/ta/construction-erp-software/chennai' },
  { label: 'Security', href: '/security' },
  // The Tamil form, not the English one. A Tamil reader who gets this far
  // must not be handed *Full name / Company / firm*.
  { label: 'தொடர்பு',   href: contactHref('demo', 'nav-ta', 'ta') },
];

const FOOTER_GROUPS_TA: typeof FOOTER_GROUPS = [
  {
    title: 'தமிழ்',
    links: [
      { label: 'முகப்பு',  href: '/ta' },
      { label: 'Modules', href: '/ta/modules' },
      { label: 'விலை',    href: '/ta/pricing' },
    ],
  },
  {
    title: 'ஊர் வாரியா',
    links: CITY_NAV.map((c) => ({ label: c.cityTa, href: `/ta/construction-erp-software/${c.slug}` })),
  },
  {
    title: 'Company',
    links: [
      { label: 'About',   href: '/about' },
      { label: 'தொடர்பு', href: contactHref('demo', 'footer-ta', 'ta') },
      { label: 'Sign in', href: '/login' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Security',       href: '/security' },
      { label: 'Privacy',        href: '/privacy' },
      { label: 'Terms',          href: '/terms' },
      { label: 'Cookie policy',  href: '/cookie-policy' },
    ],
  },
];

// Three strongest honest trust chips (every item is a real capability).
const FOOTER_TRUST = ['GST-ready', 'Role-based access', 'Built for Indian builders'];

// Legal/informational routes shouldn't push conversion — hide the footer demo CTA there.
const LEGAL_ROUTES = ['/privacy', '/terms', '/cookie-policy', '/account-deletion'];

export default function MarketingShell(
  { children, lang = 'en' }: { children: ReactNode; lang?: 'en' | 'ta' },
) {
  const pathname = usePathname();
  const isLegalPage = LEGAL_ROUTES.includes(pathname || '');
  const ta = lang === 'ta';
  const navLinks = ta ? NAV_LINKS_TA : NAV_LINKS;
  const footerGroups = ta ? FOOTER_GROUPS_TA : FOOTER_GROUPS;
  const localBusiness = localBusinessJsonLd();
  // The counterpart of the page being viewed, for the language toggle.
  //
  // Only part of the English tree is translated, so the toggle is hidden where
  // there is nothing to toggle to. It used to be rendered unconditionally and
  // linked to /ta + the current path, which on /privacy meant a 404 carrying
  // rel="alternate" — a dead end for the reader and a false signal to Google.
  const here = pathname || '/';
  const showLangToggle = ta || hasTamilVersion(here);
  const otherLangHref = ta ? toEnglishPath(here) : toTamilPath(here);
  const t = MARKETING_TOKENS;
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <Shadcn
      className="min-h-screen"
      style={{ backgroundColor: t.BG, color: t.TEXT, fontFamily: 'var(--font-inter), Inter, -apple-system, BlinkMacSystemFont, sans-serif' }}
    >
      {/* Site-wide JSON-LD: Organization + WebSite. */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteJsonLd) }} />
      {/* Local pack eligibility. Returns null until BUSINESS_CONTACT carries a
          real postal address, so nothing is emitted while it is unset. */}
      {localBusiness && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusiness) }} />
      )}

      {/* ─── Sticky nav ─── */}
      <header
        role="banner"
        className="sticky top-0 z-10 backdrop-blur-[16px]"
        style={{ backgroundColor: MARKETING_BG_TRANSLUCENT, borderBottom: `1px solid ${t.BORDER}`, boxShadow: '0 8px 28px -22px rgba(0,0,0,0.35)' }}
      >
        <nav aria-label="Primary" className="mx-auto flex w-full max-w-[1200px] items-center justify-between px-5 py-[18px] sm:px-6">
          <Link href="/" className="flex items-center gap-[10px] no-underline" style={{ color: 'inherit' }}>
            <BrandSymbol size={36} style={{ fill: t.PRIMARY }} />
            <span style={{ fontFamily: SERIF, fontWeight: 600, letterSpacing: '-0.015em', color: t.TEXT }} className="text-[17px] sm:text-[19px]">
              BuilderSiteNow
            </span>
          </Link>

          {/* Six links plus Sign in plus the CTA no longer fit at 768 — the
              desktop nav starts at lg, and the drawer covers everything below. */}
          <div className="hidden items-center gap-0.5 lg:flex">
            {navLinks.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                className={`rounded-md px-3.5 py-2 font-semibold no-underline transition-colors ${ta ? 'text-[15px]' : 'text-[14px]'}`}
                style={{ color: t.TEXT }}
                onMouseEnter={(e) => { e.currentTarget.style.color = t.PRIMARY_DARK; e.currentTarget.style.backgroundColor = PRIMARY_TINT; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = t.TEXT; e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Language toggle. Always visible, including on a phone — a Tamil
                reader who lands on an English page should not have to open a
                drawer to find their language, and hreflang alone only helps
                the ones who arrived from Google. `hrefLang` + `rel=alternate`
                make the pairing legible to a crawler following links too. */}
            {showLangToggle && (
            <Link
              href={otherLangHref}
              hrefLang={ta ? 'en-IN' : 'ta-IN'}
              rel="alternate"
              lang={ta ? 'en' : 'ta'}
              className="inline-flex items-center rounded-full px-3.5 py-2.5 text-[13.5px] font-semibold no-underline transition-colors"
              style={{ color: t.TEXT_MUTED, border: `1px solid ${t.BORDER}` }}
            >
              {ta ? 'English' : 'தமிழ்'}
            </Link>
            )}
            <Link href="/login" className="hidden items-center py-2 text-[13.5px] font-semibold no-underline transition-colors hover:opacity-100 sm:inline-flex" style={{ color: t.TEXT_MUTED }}>
              Sign in
            </Link>
            <Link
              href={contactHref('demo', ta ? 'header-ta' : 'header', ta ? 'ta' : 'en')}
              className="group hidden items-center gap-1.5 rounded-full px-5 py-2.5 text-[13.5px] font-semibold no-underline transition-all sm:inline-flex"
              style={{ background: t.PRIMARY, color: t.ON_PRIMARY, boxShadow: '0 4px 12px rgba(var(--mk-primary-rgb,4,120,87),0.18)' }}
            >
              Book a demo <ArrowRight className="h-4 w-4" />
            </Link>

            {/* Mobile hamburger */}
            <button
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              aria-expanded={open}
              className="flex h-10 w-10 items-center justify-center rounded-md lg:hidden"
              style={{ color: t.TEXT, border: `1px solid ${t.BORDER}`, backgroundColor: t.SURFACE }}
            >
              <Menu className="h-[22px] w-[22px]" />
            </button>
          </div>
        </nav>
      </header>

      {/* ─── Mobile drawer ─── */}
      {/*
        `overflow-hidden` is load-bearing, not cosmetic. The panel inside is
        parked off-canvas with translate-x-full, and a fixed element's
        overflowing child still widens the document — so at 768px the closed
        drawer reached x=1128 and /vs-spreadsheet and /vs-tally scrolled
        sideways on a tablet. Clipping it here fixes every marketing page at
        once, which is the right place for a fault the shell introduces.
      */}
      <div className={`fixed inset-0 z-50 overflow-hidden lg:hidden ${open ? '' : 'pointer-events-none'}`} aria-hidden={!open}>
        <div onClick={close} className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`} />
        <div
          className={`absolute right-0 top-0 flex h-full w-[85vw] max-w-[360px] flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
          style={{ backgroundColor: t.BG, borderLeft: `1px solid ${t.BORDER}` }}
        >
          <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: `1px solid ${t.BORDER}` }}>
            <div className="flex items-center gap-[10px]">
              <BrandSymbol size={32} style={{ fill: t.PRIMARY }} />
              <span style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em', color: t.TEXT }}>BuilderSiteNow</span>
            </div>
            <button onClick={close} aria-label="Close menu" className="flex h-9 w-9 items-center justify-center rounded-md" style={{ color: t.TEXT_MUTED }}>
              <X className="h-[22px] w-[22px]" />
            </button>
          </div>

          <div className="flex flex-1 flex-col gap-0.5 p-3">
            {navLinks.map((l) => (
              <Link
                key={l.href}
                href={l.href}
                onClick={close}
                className="rounded-md px-4 py-3 text-[16px] font-medium no-underline"
                style={{ color: t.TEXT }}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex flex-col gap-3 p-5" style={{ borderTop: `1px solid ${t.BORDER}`, backgroundColor: t.SURFACE_ALT }}>
            <Link
              href="/login"
              onClick={close}
              className="rounded-full py-3 text-center text-[14px] font-semibold no-underline"
              style={{ color: t.TEXT, border: `1px solid ${t.BORDER}`, backgroundColor: t.SURFACE }}
            >
              Sign in
            </Link>
            <Link
              href={contactHref('demo', ta ? 'mobile-nav-ta' : 'mobile-nav', ta ? 'ta' : 'en')}
              onClick={close}
              className="inline-flex items-center justify-center gap-1.5 rounded-full py-3 text-[14px] font-semibold no-underline"
              style={{ background: t.PRIMARY, color: t.ON_PRIMARY, boxShadow: '0 8px 18px rgba(var(--mk-primary-rgb,4,120,87),0.22)' }}
            >
              Book a demo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      <main>{children}</main>

      {/* ═══════════════════ FOOTER ═══════════════════ */}
      <footer role="contentinfo" style={{ borderTop: `1px solid ${t.BORDER}`, backgroundColor: t.SURFACE_ALT }}>
        <div className="mx-auto w-full max-w-[1200px] px-5 py-16 sm:px-6 md:py-[72px]">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-[300px_repeat(4,minmax(0,1fr))] lg:gap-x-10 lg:gap-y-0">
            {/* brand block */}
            <div className="max-w-[320px] sm:col-span-2 lg:col-auto">
              <Link href="/" className="mb-4 inline-flex items-center gap-[10px] no-underline">
                <BrandSymbol size={30} style={{ fill: t.PRIMARY }} />
                <span style={{ fontFamily: SERIF, fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', color: t.TEXT }}>BuilderSiteNow</span>
              </Link>
              <p className="mb-3 text-[14px] leading-relaxed" style={{ color: t.TEXT_MUTED }}>
                The construction ERP built for Indian builders — from lead to handover, on one platform.
              </p>
              <p className="mb-5 text-[13px] font-medium italic" style={{ fontFamily: SERIF, color: t.TEXT_SOFT }}>
                Purpose-built for construction. Not generic ERP.
              </p>
              <div className="flex flex-col gap-1.5">
                <span className="text-[12px] font-bold" style={{ color: t.TEXT }}>Questions?</span>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex w-fit items-center gap-2 text-[13.5px] no-underline" style={{ color: t.TEXT_MUTED }}>
                  <Mail className="h-[17px] w-[17px]" style={{ color: t.PRIMARY }} />
                  {SUPPORT_EMAIL}
                </a>
                {/* The buyer here calls before they fill in a form, so the
                    number belongs in the sitewide chrome rather than only on
                    /contact. Renders nothing until BUSINESS_CONTACT.phone is
                    set — a placeholder number would be worse than none. */}
                {hasBusinessPhone() && (
                  <a href={`tel:${BUSINESS_CONTACT.phone}`} className="inline-flex w-fit items-center gap-2 text-[13.5px] no-underline" style={{ color: t.TEXT_MUTED }}>
                    <Phone className="h-[17px] w-[17px]" style={{ color: t.PRIMARY }} />
                    {businessPhoneDisplay()}
                  </a>
                )}
                {/* WhatsApp, where this buyer actually starts a conversation.
                    It needs no Google Business Profile and no address — one
                    number in BUSINESS_CONTACT.whatsapp and every page gains a
                    one-tap path to a human. Dark until that number exists. */}
                {hasBusinessWhatsApp() && (
                  <a
                    href={whatsAppHref(ta
                      ? 'வணக்கம், BuilderSiteNow பற்றி தெரிஞ்சுக்கணும்.'
                      : 'Hi, I would like to know more about BuilderSiteNow.')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex w-fit items-center gap-2 text-[13.5px] no-underline"
                    style={{ color: t.TEXT_MUTED }}
                  >
                    <MessageCircle className="h-[17px] w-[17px]" style={{ color: '#25D366' }} />
                    {ta ? 'WhatsApp-ல பேசலாம்' : 'Chat on WhatsApp'}
                  </a>
                )}
              </div>
            </div>

            {footerGroups.map((group) => (
              <div key={group.title}>
                {/* Tamil is unicase, so `uppercase` does nothing to it — but the
                    0.1em tracking that reads as "eyebrow" in Latin actively
                    pulls apart the conjuncts and vowel marks that make a Tamil
                    word legible, and 11px is below the floor for a script with
                    marks above AND below the baseline. Measured at 11px in the
                    browser, which is how this was found. */}
                <div
                  className={ta ? 'mb-4 text-[13.5px] font-bold' : 'mb-4 text-[11px] font-bold uppercase tracking-[0.1em]'}
                  style={{ color: t.TEXT }}
                >
                  {group.title}
                </div>
                <div className="flex flex-col gap-3">
                  {group.links.map((l) => (
                    <Link key={l.href} href={l.href} className="w-fit text-[14.5px] font-medium no-underline transition-colors" style={{ color: t.TEXT }}>
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* trust chips */}
          <div className="mt-10 flex flex-wrap gap-2 pt-8 md:mt-11" style={{ borderTop: `1px solid ${t.BORDER}` }}>
            {FOOTER_TRUST.map((c) => (
              <div key={c} className="flex items-center gap-1.5 rounded-full px-3 py-1.5" style={{ border: `1px solid ${t.BORDER}`, backgroundColor: t.SURFACE }}>
                <Check className="h-[13px] w-[13px]" style={{ color: t.PRIMARY }} />
                <span className="text-[12px] font-semibold" style={{ color: t.TEXT }}>{c}</span>
              </div>
            ))}
          </div>

          {/* bottom bar */}
          <div className="mt-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center sm:gap-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-[13px]" style={{ color: t.TEXT_MUTED }}>© {new Date().getFullYear()} BuilderSiteNow</span>
              {['Made in India 🇮🇳', 'Construction ERP', 'GST Ready'].map((x) => (
                <div key={x} className="flex items-center gap-x-4">
                  <span aria-hidden className="h-[3px] w-[3px] rounded-full" style={{ backgroundColor: t.BORDER }} />
                  <span className="text-[12.5px] tracking-[0.03em]" style={{ color: t.TEXT_MUTED }}>{x}</span>
                </div>
              ))}
            </div>
            {!isLegalPage && (
              <Link href={contactHref('demo', ta ? 'footer-cta-ta' : 'footer-cta', ta ? 'ta' : 'en')} className="inline-flex items-center gap-1 whitespace-nowrap text-[13px] font-bold no-underline" style={{ color: t.PRIMARY }}>
                {ta ? 'பாக்கணுமா? Demo கேளுங்க →' : 'Ready to see it? Book a demo →'}
              </Link>
            )}
          </div>
        </div>
      </footer>
    </Shadcn>
  );
}
